import {filter, map} from 'lodash';
import {useMemo, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {userApi} from 'api';
import {Select} from 'components/UIKit';
import {useApiData} from 'hooks';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {UserSummary} from 'types';
import {userAuthUtils} from 'utils';
import {getUserName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.USER_SELECTOR;

type OptionValue = string | number;

type Props<T extends OptionValue> = {
    value: T;
    users?: UserSummary[];
    className?: string;
    showEmptyOption?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    required?: boolean;
    context?: 'user' | 'agent' | 'assistant';
    field?: 'eid' | 'email';
    onChange: (field: T) => void;
    renderOptions?: (users: UserSummary[]) => (Option<string, string> | Option<number, string>)[]
}

export type UserSelectorProps<T extends OptionValue> = Props<T>;

export const UserSelector = <T extends OptionValue, >({
    value, users: usersProp, showEmptyOption = true, errorMessage, required, className,
    context = 'user', field = 'eid', hasError, onChange, renderOptions
}: Props<T>) => {
    const {t} = useTranslation();
    const localUser = useMemo(() => userAuthUtils.getLocalUser(), []);
    const placeholder = useMemo(() => {
        if (context === 'user') {
            return t(T_KEYS.SELECT_USER);
        } else if (context === 'agent') {
            return t(T_KEYS.SELECT_AGENT);
        } else if (context === 'assistant') {
            return t(T_KEYS.SELECT_ASSISTANT);
        } else {
            return null;
        }
    },
    [t, context]);

    const {send: fetchUsers, data: usersFromRequest = []} = useApiData<UserSummary[]>(userApi.getUsersSummary);
    const users = useMemo(() => filter(usersProp || usersFromRequest || [], {activated: true}), [usersProp, usersFromRequest]);

    const userOptions: Option<number | string, string>[] = useMemo(() => {
        if (renderOptions) {
            return renderOptions(users);
        }
        const options = map(users, (user) => ({
            id: user[field],
            name: getUserName(t, user, user[field] === localUser[field])
        }));

        if (showEmptyOption) {
            options.unshift({
                id: null,
                name: t(T_KEYS.NONE)
            });
        }

        return options;
    }, [t, users, localUser, field, showEmptyOption, renderOptions]);

    useEffect(() => {
        if (!usersProp) {
            fetchUsers();
        }
    }, [usersProp, fetchUsers]);

    return (
        <Select
            value={value}
            required={required}
            placeholder={placeholder}
            errorMessage={errorMessage}
            hasError={hasError}
            options={userOptions}
            className={className}
            valueAs='id'
            onChange={onChange}
        />
    );
};